import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { General } from "../../util/General";
import LocalStorageUtil from "../../util/LocalStorageUtil";
import { LocalStorageConstants } from "../../constants/ApplicationConstants/LocalStorageConstants";

import type { LocationRecordData } from "../../shared/interface/MasterManagement/locationMaster.interface";
import type { CustomerData } from "../../shared/interface/MasterManagement/customerMaster.interface";
import { masterFiltersInterface } from "../../shared/interface/MasterManagement/masterManagementCommon.interface";
import Authentication from "../../util/Authentication";
import { UserInterface } from "../../shared/interface/auth/User.interface";
import { ModuleAndIdMapping, Modules, Operations } from "../../constants/DatabaseConstants/MasterConstant";

interface CommonState {
  navCollapsed: boolean;
  width: number;
  pathname: string;
  renderLogo: boolean;
  reRenderSideBar: number | null;
  reRenderTopBar: number | null;
  customerList: CustomerData[] | null;
  locationList: LocationRecordData[] | null;
  masterFilters: masterFiltersInterface | null;
  openCount: number;
}

const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;
const topBarFilter = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.MASTER_FILTERS);
const defaultId = Authentication.hasModuleAndOperationAccess(userData, Modules.CUSTOMER_MASTER, Operations.READ) ? ModuleAndIdMapping[Modules.CUSTOMER_MASTER] : null;

const initialState: CommonState = {
  navCollapsed: false,
  width: window.innerWidth,
  pathname: "/",
  renderLogo: false,
  reRenderSideBar: null,
  reRenderTopBar: null,
  customerList: [],
  locationList: [],
  masterFilters: {
    customerId: topBarFilter?.customerId ?? null,
    locationId: topBarFilter?.locationId ?? null,
    clientId: topBarFilter?.clientId ?? null,
    keyFobId: topBarFilter?.keyFobId ?? null,
    moduleId: topBarFilter?.moduleId ?? defaultId,
    refresh: true
  },
  openCount: 0
};

const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    updateWindowWidth(state, action: PayloadAction<number>) {
      state.width = action.payload;
    },
    toggleCollapsedSideNav(state, action: PayloadAction<boolean>) {
      state.navCollapsed = action.payload;
    },
    reRenderSideBar(state) {
      state.reRenderSideBar = General.getGUID();
    },
    reRenderTopBar(state) {
      state.reRenderTopBar = General.getGUID();
    },
    clearReduxStoreAction() {
      return initialState;
    },
    updateCustomerList(state, action: PayloadAction<CustomerData[] | null>) {
      state.customerList = action.payload;
    },
    updateLocationList(state, action: PayloadAction<LocationRecordData[] | null>) {
      state.locationList = action.payload;
    },
    updateMasterFilters(state, action: PayloadAction<masterFiltersInterface | null>) {
      if (action.payload) {
        state.masterFilters = {
          ...state.masterFilters,
          ...action.payload
        };
      }
    },
    updateOpenCount(state, action: PayloadAction<number>) {
      state.openCount = action.payload;
    }
  }
});

export const { updateWindowWidth, toggleCollapsedSideNav, reRenderSideBar, reRenderTopBar, clearReduxStoreAction, updateCustomerList, updateLocationList, updateMasterFilters, updateOpenCount } = commonSlice.actions;

export default commonSlice.reducer;
